import React from 'react';
import Layout from './Layout';
import { Zap, TrendingDown, Gauge, AlertTriangle } from 'lucide-react';

const BiogasIntegration: React.FC = () => {
  const integrationMetrics = [
    {
      title: 'Biogas Production',
      value: '1,234 MWh',
      change: '+15.2%',
      icon: Zap,
      color: 'text-green-600'
    },
    {
      title: 'Coal Displacement',
      value: '892 tons',
      change: '-18.7%',
      icon: TrendingDown,
      color: 'text-red-600'
    },
    {
      title: 'EAF Efficiency',
      value: '94.2%',
      change: '+3.1%',
      icon: Gauge,
      color: 'text-blue-600'
    }
  ];

  const steelPlants = [
    {
      name: 'Steel Plant A - Mumbai',
      biogasUsage: '567 MWh',
      coalReduction: '45%',
      efficiency: 94.2,
      co2Avoided: '1,247 tCO₂e',
      status: 'Optimal',
      lastUpdate: '2 hours ago'
    },
    {
      name: 'Steel Plant C - Pune',
      biogasUsage: '423 MWh',
      coalReduction: '38%',
      efficiency: 91.8,
      co2Avoided: '892 tCO₂e',
      status: 'Good',
      lastUpdate: '1 hour ago'
    },
    {
      name: 'Steel Plant E - Jamshedpur',
      biogasUsage: '244 MWh',
      coalReduction: '28%',
      efficiency: 87.5,
      co2Avoided: '534 tCO₂e',
      status: 'Maintenance',
      lastUpdate: '6 hours ago'
    }
  ];

  const processSteps = [
    {
      step: 'Biogas Generation',
      description: 'Anaerobic digestion of organic waste produces methane-rich biogas',
      metrics: { input: '2,847 tons organic waste', output: '1,234 MWh biogas' },
      status: 'active'
    },
    {
      step: 'Gas Purification',
      description: 'Remove impurities and upgrade biogas to meet steel industry standards',
      metrics: { purity: '97.8% CH₄', pressure: '15 bar' },
      status: 'active'
    },
    {
      step: 'EAF Integration',
      description: 'Inject purified biogas into Electric Arc Furnaces for steel production',
      metrics: { injection_rate: '45 m³/min', temperature: '1,650°C' },
      status: 'active'
    },
    {
      step: 'Coal Displacement',
      description: 'Replace traditional coal with biogas reducing carbon footprint',
      metrics: { coal_saved: '892 tons', co2_avoided: '2,673 tCO₂e' },
      status: 'active'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Optimal':
        return 'bg-green-100 text-green-800';
      case 'Good':
        return 'bg-blue-100 text-blue-800';
      case 'Maintenance':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Biogas → Steel Production Integration</h1>
          <div className="text-sm text-gray-500">
            Real-time EAF Monitoring
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {integrationMetrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div key={index} className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                  </div>
                  <Icon className={`h-8 w-8 ${metric.color}`} />
                </div>
                <div className="mt-4 flex items-center">
                  <span className={`text-sm font-medium ${
                    metric.title === 'Coal Displacement' ? 'text-green-600' : 'text-blue-600'
                  }`}>
                    {metric.change}
                  </span>
                  <span className="text-sm text-gray-500 ml-2">vs last month</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Biogas Integration Process</h2>
          <div className="space-y-6">
            {processSteps.map((process, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-green-600">{index + 1}</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900">{process.step}</h3>
                  <p className="text-sm text-gray-600 mt-1">{process.description}</p>
                  <div className="mt-3 grid grid-cols-2 gap-4">
                    {Object.entries(process.metrics).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          {key.replace('_', ' ')}
                        </p>
                        <p className="text-sm font-medium text-gray-900">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Steel Plant Performance</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Biogas Usage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coal Reduction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Efficiency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CO₂ Avoided
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Update
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {steelPlants.map((plant, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{plant.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      {plant.biogasUsage}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                      -{plant.coalReduction}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">
                      {plant.efficiency}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      {plant.co2Avoided}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(plant.status)}`}>
                        {plant.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {plant.lastUpdate}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Technical Specifications</h2>
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Biogas Composition</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Methane (CH₄):</span>
                    <span className="ml-2 font-medium">97.8%</span>
                  </div>
                  <div>
                    <span className="text-gray-500">CO₂:</span>
                    <span className="ml-2 font-medium">1.8%</span>
                  </div>
                  <div>
                    <span className="text-gray-500">H₂S:</span>
                    <span className="ml-2 font-medium">&lt;10 ppm</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Calorific Value:</span>
                    <span className="ml-2 font-medium">9.8 kWh/m³</span>
                  </div>
                </div>
              </div>
              
              <div className="border rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">EAF Integration Parameters</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Injection Pressure:</span>
                    <span className="ml-2 font-medium">15 bar</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Flow Rate:</span>
                    <span className="ml-2 font-medium">45 m³/min</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Operating Temp:</span>
                    <span className="ml-2 font-medium">1,650°C</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Efficiency Gain:</span>
                    <span className="ml-2 font-medium">+3.2%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Environmental Benefits</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                <div>
                  <h3 className="font-medium text-green-900">Coal Dependency Reduction</h3>
                  <p className="text-sm text-green-700">45% reduction in coal usage across steel plants</p>
                </div>
                <div className="text-2xl font-bold text-green-600">-45%</div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div>
                  <h3 className="font-medium text-blue-900">CO₂ Emissions Avoided</h3>
                  <p className="text-sm text-blue-700">Significant reduction in carbon footprint</p>
                </div>
                <div className="text-lg font-bold text-blue-600">2,673 tCO₂e</div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div>
                  <h3 className="font-medium text-purple-900">Renewable Energy Integration</h3>
                  <p className="text-sm text-purple-700">Clean energy from waste-derived biogas</p>
                </div>
                <div className="text-lg font-bold text-purple-600">1,234 MWh</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">System Alerts & Monitoring</h2>
          <div className="space-y-3">
            <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-green-800">
                  Steel Plant A: Biogas injection rate optimized - efficiency increased to 94.2%
                </p>
                <p className="text-xs text-green-600">15 minutes ago</p>
              </div>
            </div>
            
            <div className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-yellow-800">
                  Steel Plant E: Scheduled maintenance affecting biogas integration - temporary coal backup activated
                </p>
                <p className="text-xs text-yellow-600">2 hours ago</p>
              </div>
            </div>
            
            <div className="flex items-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-blue-800">
                  Biogas purification system: 97.8% methane purity maintained across all plants
                </p>
                <p className="text-xs text-blue-600">1 hour ago</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BiogasIntegration;